import React, { useState, useEffect } from 'react';
import api from '../config/axios';
import toast from 'react-hot-toast';
import {
  CreditCardIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  CheckIcon,
  XMarkIcon,
  ArrowUpCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

const AdminSubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [packageFilter, setPackageFilter] = useState('');
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeTarget, setUpgradeTarget] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });

  useEffect(() => {
    fetchSubscriptions();
    fetchPlans();
  }, [pagination.page, searchTerm, statusFilter, packageFilter]);

  const fetchSubscriptions = async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(packageFilter && { packageType: packageFilter })
      });

      const response = await api.get(`/admin/subscriptions?${params}`);
      setSubscriptions(response.data.subscriptions);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || response.data.total || 0
      }));
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await api.get('/admin/plans');
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const updateSubscriptionStatus = async (subscriptionId, status) => {
    try {
      await api.put(`/admin/subscriptions/${subscriptionId}/status`, { status });
      toast.success(`Subscription status updated to ${status}`);
      fetchSubscriptions();
    } catch (error) {
      toast.error('Failed to update subscription status');
    }
  };

  const openUpgradeModal = (subscription) => {
    setUpgradeTarget(subscription);
    setSelectedPlanId(null);
    setShowUpgradeModal(true);
  };

  const upgradeSubscription = async () => {
    if (!selectedPlanId || !upgradeTarget) return;

    setUpgrading(true);
    try {
      const response = await api.put(`/admin/subscriptions/${upgradeTarget.id}/upgrade`, {
        planId: selectedPlanId
      });
      toast.success(response.data.message || 'Subscription upgraded successfully');
      setShowUpgradeModal(false);
      setUpgradeTarget(null);
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to upgrade subscription');
    } finally {
      setUpgrading(false);
    }
  };

  const openSubscriptionDetails = (subscription) => {
    setSelectedSubscription(subscription);
    setShowDetailsModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount, currency = 'ZAR') => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: currency,
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-red-100 text-red-800',
      expired: 'bg-gray-100 text-gray-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getPackageBadge = (slug) => {
    const badges = {
      starter: 'bg-blue-100 text-blue-800',
      professional: 'bg-purple-100 text-purple-800',
      enterprise: 'bg-indigo-100 text-indigo-800',
      ultimate: 'bg-red-100 text-red-800'
    };
    return badges[slug] || 'bg-gray-100 text-gray-800';
  };

  // Helper to get display name — handles both backend shapes
  const getOwnerName = (sub) => {
    if (sub.owner?.name) return sub.owner.name;
    if (sub.user?.firstName) return `${sub.user.firstName} ${sub.user.lastName}`;
    return sub.tenantName || 'N/A';
  };

  const getOwnerEmail = (sub) => {
    return sub.owner?.email || sub.user?.email || '';
  };

  const getPlanName = (sub) => sub.planName || sub.packageName || 'N/A';
  const getPlanSlug = (sub) => sub.planSlug || sub.packageType || '';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-neutral-800">Manage Subscriptions</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Upgrade, activate, or cancel client subscriptions ({pagination.total} total)
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl border border-neutral-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <input
              type="text"
              placeholder="Search by tenant or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2.5 w-full text-sm border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2.5 text-sm border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="cancelled">Cancelled</option>
            <option value="expired">Expired</option>
          </select>
          <select
            value={packageFilter}
            onChange={(e) => setPackageFilter(e.target.value)}
            className="px-3 py-2.5 text-sm border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="">All Plans</option>
            {plans.map(p => (
              <option key={p.id} value={p.slug}>{p.name}</option>
            ))}
          </select>
          <button
            onClick={() => { setSearchTerm(''); setStatusFilter(''); setPackageFilter(''); }}
            className="px-4 py-2.5 text-sm text-neutral-600 hover:text-neutral-800 hover:bg-neutral-50 rounded-lg transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Subscriptions Table */}
      <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">Client</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">Plan</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">Period</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-neutral-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {subscriptions.map((subscription) => (
                <tr key={subscription.id} className="hover:bg-neutral-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
                        <CreditCardIcon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-neutral-800">{getOwnerName(subscription)}</div>
                        <div className="text-xs text-neutral-500">{getOwnerEmail(subscription)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full ${getPackageBadge(getPlanSlug(subscription))}`}>
                      {getPlanName(subscription)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full ${getStatusBadge(subscription.status)}`}>
                      {subscription.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-800">
                    {formatCurrency(subscription.price, subscription.currency)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                    <div>
                      <div>{formatDate(subscription.startDate)}</div>
                      {subscription.endDate && (
                        <div className="text-xs text-neutral-400">to {formatDate(subscription.endDate)}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openSubscriptionDetails(subscription)}
                        className="p-2 text-neutral-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openUpgradeModal(subscription)}
                        className="p-2 text-neutral-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title="Change Plan"
                      >
                        <ArrowUpCircleIcon className="h-4 w-4" />
                      </button>
                      {subscription.status === 'pending' && (
                        <button
                          onClick={() => updateSubscriptionStatus(subscription.id, 'active')}
                          className="p-2 text-neutral-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Activate"
                        >
                          <CheckIcon className="h-4 w-4" />
                        </button>
                      )}
                      {(subscription.status === 'active' || subscription.status === 'pending') && (
                        <button
                          onClick={() => {
                            if (window.confirm(`Cancel subscription for ${getOwnerName(subscription)}?`)) {
                              updateSubscriptionStatus(subscription.id, 'cancelled');
                            }
                          }}
                          className="p-2 text-neutral-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Cancel"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {subscriptions.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-sm text-neutral-500">
                    No subscriptions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total > pagination.limit && (
          <div className="bg-white px-6 py-3 flex items-center justify-between border-t border-neutral-200">
            <p className="text-sm text-neutral-500">
              Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg hover:bg-neutral-50 disabled:opacity-50 transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page * pagination.limit >= pagination.total}
                className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg hover:bg-neutral-50 disabled:opacity-50 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Subscription Details Modal */}
      {showDetailsModal && selectedSubscription && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-neutral-900/60 backdrop-blur-sm" onClick={() => setShowDetailsModal(false)} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-semibold text-neutral-800">Subscription Details</h3>
                <button onClick={() => setShowDetailsModal(false)} className="p-1 text-neutral-400 hover:text-neutral-600 rounded-lg">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-neutral-500 uppercase">Client</label>
                  <p className="mt-1 text-sm font-medium text-neutral-800">{getOwnerName(selectedSubscription)}</p>
                  <p className="text-xs text-neutral-500">{getOwnerEmail(selectedSubscription)}</p>
                  {selectedSubscription.tenantName && (
                    <p className="text-xs text-neutral-400">Tenant: {selectedSubscription.tenantName}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-neutral-500 uppercase">Plan</label>
                    <span className={`mt-1 inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full ${getPackageBadge(getPlanSlug(selectedSubscription))}`}>
                      {getPlanName(selectedSubscription)}
                    </span>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-neutral-500 uppercase">Status</label>
                    <span className={`mt-1 inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full ${getStatusBadge(selectedSubscription.status)}`}>
                      {selectedSubscription.status}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-neutral-500 uppercase">Amount</label>
                    <p className="mt-1 text-sm text-neutral-800">{formatCurrency(selectedSubscription.price, selectedSubscription.currency)}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-neutral-500 uppercase">Auto Renew</label>
                    <p className="mt-1 text-sm text-neutral-800">{selectedSubscription.autoRenew ? 'Yes' : 'No'}</p>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 uppercase">Period</label>
                  <p className="mt-1 text-sm text-neutral-800">
                    {formatDate(selectedSubscription.startDate)} &mdash; {selectedSubscription.endDate ? formatDate(selectedSubscription.endDate) : 'Ongoing'}
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-neutral-200">
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    openUpgradeModal(selectedSubscription);
                  }}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
                >
                  <ArrowUpCircleIcon className="h-4 w-4" />
                  Change Plan
                </button>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 text-sm font-medium text-neutral-600 bg-neutral-100 rounded-lg hover:bg-neutral-200 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade / Change Plan Modal */}
      {showUpgradeModal && upgradeTarget && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-neutral-900/60 backdrop-blur-sm" onClick={() => setShowUpgradeModal(false)} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-neutral-800">Change Subscription Plan</h3>
                <button onClick={() => setShowUpgradeModal(false)} className="p-1 text-neutral-400 hover:text-neutral-600 rounded-lg">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <p className="text-sm text-neutral-500 mb-5">
                Change the plan for <strong>{getOwnerName(upgradeTarget)}</strong>
                {upgradeTarget.tenantName && <span> ({upgradeTarget.tenantName})</span>}.
                Currently on <strong>{getPlanName(upgradeTarget)}</strong>.
              </p>

              {/* Plan selection */}
              <div className="space-y-3 mb-6">
                {plans.filter(p => p.is_active).map(plan => {
                  const isCurrent = plan.slug === getPlanSlug(upgradeTarget);
                  const isSelected = selectedPlanId === plan.id;

                  return (
                    <button
                      key={plan.id}
                      onClick={() => !isCurrent && setSelectedPlanId(plan.id)}
                      disabled={isCurrent}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50'
                          : isCurrent
                          ? 'border-neutral-200 bg-neutral-50 opacity-60 cursor-not-allowed'
                          : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-neutral-800">{plan.name}</span>
                            {isCurrent && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-neutral-200 text-neutral-600 rounded-full">
                                Current
                              </span>
                            )}
                          </div>
                          <div className="mt-1 text-xs text-neutral-500">
                            {plan.message_limit ? `${plan.message_limit.toLocaleString()} msgs/mo` : 'Unlimited messages'}
                            {' | '}
                            {plan.workspace_limit === -1 ? 'Unlimited' : plan.workspace_limit} workspace{plan.workspace_limit !== 1 ? 's' : ''}
                            {' | '}
                            {plan.max_team_members === -1 ? 'Unlimited' : plan.max_team_members} team member{plan.max_team_members !== 1 ? 's' : ''}
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-lg font-bold text-neutral-800">
                            {formatCurrency(plan.price, plan.currency || 'ZAR')}
                          </span>
                          <span className="text-xs text-neutral-500">/{plan.duration_days}d</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200">
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  disabled={upgrading}
                  className="px-4 py-2 text-sm font-medium text-neutral-600 bg-neutral-100 rounded-lg hover:bg-neutral-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={upgradeSubscription}
                  disabled={!selectedPlanId || upgrading}
                  className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {upgrading ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                      Upgrading...
                    </>
                  ) : (
                    <>
                      <ArrowUpCircleIcon className="h-4 w-4" />
                      Change Plan
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSubscriptions;
